/**
 * Filter Model Utilities
 *
 * Utilities for converting between MUI DataGrid filter models and backend query formats.
 * These utilities handle:
 * - Converting MUI filter operators to backend operators
 * - Determining field types (text, date, number, boolean)
 * - Converting query objects to filter models for restoring saved searches
 */

/**
 * Date columns used for employee data
 */
export const dateColumns = [
  '_createdAt',
  '_updatedAt',
  '_startDate',
  'dateOfBirth',
  'partner.dateOfBirth',
  'regulationSpecifics.forcedMoveDate',
  'regulationSpecifics.seniorityDate',
  'regulationSpecifics.preMissionSeniorityDate',
  'regulationSpecifics.placementEndDate',
  'regulationSpecifics.rentalAgreementEndDate',
]

/**
 * Generate a UUID v4
 */
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * Map MUI operator to backend operator
 */
const mapMuiOperatorToBackend = (muiOperator, fieldName, columnDefinitions = []) => {
  switch(muiOperator) {
    case 'contains':
      return 'contains'
    case 'equals':
    case '=':
    case 'is':
      return 'equals'
    case '!=':
    case 'not':
    case 'notEquals':
      return 'notEquals'
    case '>':
    case 'gt':
    case 'after':
      return 'gt'
    case '>=':
    case 'gte':
    case 'onOrAfter':
      return 'gte'
    case '<':
    case 'lt':
    case 'before':
      return 'lt'
    case '<=':
    case 'lte':
    case 'onOrBefore':
      return 'lte'
    case 'isEmpty':
      return 'notExists'
    case 'isNotEmpty':
      return 'exists'
    case 'isAnyOf':
      return 'in'
    case 'equalsRelative':
      return 'equals'
    case 'afterRelative':
      return 'gt'
    case 'beforeRelative':
      return 'lt'
    default: {
      // Default based on field type
      const columnDef = columnDefinitions.find(col => col.value === fieldName)
      if (columnDef && (columnDef.type === 'TYPE_DATE' || columnDef.type === 3)) {
        return 'equals'
      }
      return 'contains'
    }
  }
}

/**
 * Map backend operator to MUI operator
 */
const mapBackendOperatorToMui = (backendOperator, fieldType) => {
  switch(backendOperator) {
    case 'equals':
      return fieldType === 'date' ? 'is' : 'equals'
    case 'notEquals':
      return 'not'
    case 'gt':
      return fieldType === 'date' ? 'after' : '>'
    case 'gte':
      return fieldType === 'date' ? 'onOrAfter' : '>='
    case 'lt':
      return fieldType === 'date' ? 'before' : '<'
    case 'lte':
      return fieldType === 'date' ? 'onOrBefore' : '<='
    case 'contains':
      return 'contains'
    case 'notExists':
      return 'isEmpty'
    case 'exists':
      return 'isNotEmpty'
    case 'in':
      return 'isAnyOf'
    default:
      return backendOperator
  }
}

/**
 * Determine field type based on column definition and field name
 */
const determineFieldType = (fieldName, columnDefinitions = []) => {
  const columnDef = columnDefinitions.find(col => col.value === fieldName)
  const isDateField = dateColumns.includes(fieldName) ||
    fieldName.includes('_updated') ||
    fieldName.includes('_created') ||
    (columnDef && (columnDef.type === 'TYPE_DATE' || columnDef.type === 3))

  if (isDateField) {
    return 'date'
  } else if (columnDef) {
    if (columnDef.type === 'TYPE_NUMBER' || columnDef.type === 2) {
      return 'number'
    } else if (columnDef.type === 'TYPE_BOOLEAN' || columnDef.type === 4) {
      return 'boolean'
    }
  }
  return 'text'
}

/**
 * Convert MUI DataGrid filter model to backend query format
 *
 * @param {Object} model - MUI DataGrid filter model
 * @param {Array} columnDefinitions - Array of column definitions (optional)
 * @returns {Array} Backend query array
 */
export const convertFilterModelToQuery = (model, columnDefinitions = []) => {
  if (!model || !model.items || model.items.length === 0) {
    return []
  }

  const query = []
  model.items.forEach(item => {
    // Skip empty or partial values
    // For strings, also skip partial values that start with '_PARTIAL_'
    const isPartialValue = typeof item.value === 'string' && item.value.startsWith('_PARTIAL_')

    if (item.value !== undefined &&
        item.value !== null &&
        item.value !== '' &&
        !isPartialValue) {
      const fieldName = item.field || item.columnField
      if (!fieldName) return

      const id = generateUUID()
      const muiOperator = item.operatorValue || item.operator || 'contains'
      const backendOperator = mapMuiOperatorToBackend(muiOperator, fieldName, columnDefinitions)
      const fieldType = determineFieldType(fieldName, columnDefinitions)

      query.push({
        id,
        children: [],
        type: fieldType,
        field: fieldName,
        operator: backendOperator,
        value: item.value
      })
    }
  })

  return query
}

/**
 * Convert backend query to MUI DataGrid filter model format
 *
 * @param {Array} query - Backend query array
 * @returns {Object} MUI DataGrid filter model
 */
export const convertQueryToFilterModel = (query) => {
  if (!query || query.length === 0) {
    return { items: [] }
  }

  const items = query.map(condition => {
    const muiOperator = mapBackendOperatorToMui(condition.operator, condition.type)

    return {
      id: condition.id,
      field: condition.field,
      operator: muiOperator,
      operatorValue: muiOperator,
      value: condition.value
    }
  })

  return { items }
}
